import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Star } from "lucide-react";
import { MaskedLine, FadeUp } from "./Reveal";
import { scrollToId } from "@/lib/scroll";
import { BUSINESS, IMAGES } from "@/data/content";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative pt-20 lg:min-h-screen lg:pt-32">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 lg:grid-cols-12 lg:gap-8 lg:px-12">
        <motion.div style={{ y: textY }} className="flex flex-col justify-center lg:col-span-7 lg:pb-24">
          <FadeUp immediate={false} y={16} className="mb-8">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              data-testid="hero-kicker"
              className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-taupe"
            >
              <span className="inline-block h-2 w-2 bg-terra" />
              STAR AVG <span className="text-terra">ENTERPRISE</span> — Greater Noida
            </motion.p>
          </FadeUp>
          <h1 data-testid="hero-headline" className="font-serif text-4xl leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLine immediate delay={0.35}>Spaces, designed.</MaskedLine>
            <MaskedLine immediate delay={0.5}>Furniture, crafted.</MaskedLine>
            <MaskedLine immediate delay={0.65}>
              Homes, <em className="text-terra">delivered.</em>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 max-w-md text-base leading-relaxed text-taupe"
          >
            Interior design, modular furniture and turnkey execution — from the
            first sketch to the final fitting, under one roof.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToId("#contact"); }}
              data-testid="hero-cta-start"
              className="group flex w-full items-center justify-center gap-3 bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-clay hover:text-ink sm:w-auto"
            >
              Start a project
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#services"
              onClick={(e) => { e.preventDefault(); scrollToId("#services"); }}
              data-testid="hero-cta-services"
              className="w-full border border-line px-8 py-4 text-center text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 hover:border-ink sm:w-auto"
            >
              Explore services
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            data-testid="hero-rating-strip"
            className="mt-10 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line pt-6 text-xs uppercase tracking-[0.2em] text-taupe lg:mt-16"
          >
            <span className="flex items-center gap-1 text-terra">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={12} fill="currentColor" strokeWidth={0} />
              ))}
            </span>
            <span className="text-ink">{BUSINESS.rating} on Google</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">{BUSINESS.reviewCount} reviews</span>
          </motion.div>
        </motion.div>
        <div className="relative lg:col-span-5">
          <div className="absolute -right-4 top-10 hidden h-full w-full bg-clay/30 lg:block" aria-hidden="true" />
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.9, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden"
          >
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.hero}
              alt="Elegant modern kitchen designed and built by STAR AVG ENTERPRISE"
              data-testid="hero-image"
              className="h-[46vh] w-full scale-110 object-cover lg:h-[75vh]"
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.8 }}
            className="mt-4 text-right text-[10px] uppercase tracking-[0.3em] text-taupe"
          >
            Surajpur Site IV — Greater Noida, UP
          </motion.p>
        </div>
      </div>
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.8 }}
        onClick={() => scrollToId("#services")}
        data-testid="hero-scroll-cue"
        aria-label="Scroll to services"
        className="absolute bottom-8 left-6 hidden text-taupe transition-colors duration-300 hover:text-ink lg:left-12 lg:block"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
